import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useTheme } from "../theme/ThemeProvider";
import ThemeToggle from "../components/ThemeToggle";
import logo from "../images/muyaz_small.png";
import {
  FaUserCircle,
  FaChevronDown,
  FaSignOutAlt,
  FaSignInAlt,
  FaBars,
  FaTimes,
} from "react-icons/fa";

import { MENU } from "./menuConfig";
import HomePage from "../pages/HomePage";
import CompleteProfileModal from "./CompleteProfile";

import RoleManagement from "../pages/RoleManagement";
import UserManagement from "../pages/UserManagement";
import BranchManagement from "../pages/BranchManagement";
import ClientManagement from "../pages/ClientManagement";
import UnitManagement from "../pages/UnitManagement";
import AssetCategoryManagement from "../pages/AssetCategoryManagement";
import AssetManagement from "../pages/AssetManagement";
import CategoryManagement from "../pages/CategoryManagement";
import ProductManagement from "../pages/ProductManagement";
import ExpenseManagement from "../pages/ExpenseManagement";
import ExpenseCategoryManagement from "../pages/ExpenseCategoryManagement";
import PaymentManagement from "../pages/PaymentManagement";
import AccountChartManagement from "../pages/AccountChartManagement";
import InvoicesPage from "../pages/InvoicesPage";
import InventoryMovement from "../pages/InventoryMovement";
import SalesReport from "../pages/SalesReport";
import ReturnPage from "../pages/ReturnPage";
import StockPage from "../pages/StockPage";
import ExpiredBatch from "../pages/ExpiredBatch";
import PosPage from "../pages/PosPage";
import Reports from "../pages/FinancialReports";
import Receipt from "../components/InvoiceReceipt";
import TenantDeactivation from "../pages/TenantDeactivation";
import DepartmentManagement from "../pages/DepartmentManagement";
import JobPositionManagement from "../pages/JobPositionManagement";
import EmployeeManagement from "../pages/EmployeeManagement";
import PayrollComponntManagemnt from "../pages/PayrollComponntManagemnt";
import PayrollStructureManagement from "../pages/PayrollStructureManagement";
import EmployeeAdvanceManagement from "../pages/EmployeeAdvanceManagement";
import PayrollManagement from "../pages/PayrollManagement";
import FiscalYearManagement from "../pages/FiscalYearManagement";
import TenantSettings from "../pages/TenantSettings";
import TenantBackup from "../pages/TenantBackup";
import SubscriptionBilling from "../pages/SubscriptionBilling";
import IncomeStatement from "../components/IncomeStatement";
import BalanceSheet from "../components/BalanceSheet";
import CashFlowStatement from "../components/CashFlowStatement";
import TrialBalance from "../components/TrialBalance";
import TaxSummaryReport from "../components/TaxSummaryReport";
import WithholdingLogs from "../pages/WithholdingLogs";
import LetterManagement from "../pages/LetterManagement";

// ========================================================
// Permission Hook
// ========================================================

const getTenantBranding = (user) => {
  const tenant = user?.tenant;
  const logoUrl = tenant?.logoUrl?.trim();
  return {
    logoSrc: logoUrl || logo,
    name: tenant?.name?.trim() || "MBOS",
  };
};

const usePermissions = (user) => {
  return React.useMemo(() => {
    const allPermissions = new Set();

    if (user?.user?.roles && Array.isArray(user.user?.roles)) {
      user.user?.roles.forEach((role) => {
        if (role.permissions && Array.isArray(role.permissions)) {
          role.permissions.forEach((perm) => {
            if (perm.name) allPermissions.add(perm.name);
          });
        }
      });
    }

    const hasPermission = (permission) => allPermissions.has(permission);

    return { hasPermission };
  }, [user]);
};

// ========================================================
// Component Map
// ========================================================
const COMPONENT_MAP = {
  dashboard: <Reports />,
  pos: <PosPage />,
  sale: <InvoicesPage invoiceType="SALE" />,
  salesReport: <SalesReport />,
  purchase: <InvoicesPage invoiceType="PURCHASE" />,
  sreturns: <ReturnPage invoiceType="SALE_RETURN" />,
  preturns: <ReturnPage invoiceType="PURCHASE_RETURN" />,
  stockBalance: <StockPage />,
  adjustment: <InventoryMovement invoiceType="ALL" />,
  expired: <ExpiredBatch />,
  product: <ProductManagement />,
  productCategory: <CategoryManagement />,
  payment: <PaymentManagement />,
  expense: <ExpenseManagement />,
  expenseCat: <ExpenseCategoryManagement />,
  unit: <UnitManagement />,
  client: <ClientManagement />,
  branch: <BranchManagement />,
  user: <UserManagement />,
  role: <RoleManagement />,
  account: <AccountChartManagement />,
  receipt: <Receipt />,
  deactivate: <TenantDeactivation />,
  department: <DepartmentManagement />,
  jobPosition: <JobPositionManagement />,
  employee: <EmployeeManagement />,
  payrollComponnt: <PayrollComponntManagemnt />,
  salaryStructure: <PayrollStructureManagement />,
  payroll: <PayrollManagement />,
  advance: <EmployeeAdvanceManagement />,
  fiscalYear: <FiscalYearManagement />,
  backup: <TenantBackup />,
  tenantSetting: <TenantSettings />,
  assetCat: <AssetCategoryManagement />,
  fixedasset: <AssetManagement />,
  subscriptionBilling: <SubscriptionBilling />,
  profit: <IncomeStatement />,
  balance: <BalanceSheet />,
  trial: <TrialBalance />,
  cash: <CashFlowStatement />,
  taxSummary: <TaxSummaryReport />,
  withholdingLogs: <WithholdingLogs type="ALL" />,
  payablewithholds: <WithholdingLogs type="PAYABLE" />,
  letterManagement: <LetterManagement />,
};

// ========================================================
// Recursive Menu Filter
// ========================================================
const filterMenu = (menuItems, hasPermission) => {
  return menuItems
    .map((item) => {
      if (item.children) {
        const filteredChildren = filterMenu(item.children, hasPermission);
        if (filteredChildren.length === 0) return null;
        return { ...item, children: filteredChildren };
      }
      if (item.permission && !hasPermission(item.permission)) return null;
      return item;
    })
    .filter(Boolean);
};

// ========================================================
// Sidebar Item
// ========================================================
const SidebarItem = ({ item, activeTab, onOpenTab, hasPermission }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isActive = activeTab === item.id;
  const [isOpen, setIsOpen] = useState(false);

  if (hasChildren) {
    return (
      <div className="w-full">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-3 py-3 rounded-xl font-medium text-sm transition-all
            text-slate-500 hover:text-slate-900 hover:bg-slate-100
            dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800 group"
        >
          <div className="flex items-center gap-3">
            <span className="text-lg flex-shrink-0 text-slate-400 group-hover:text-emerald-500 dark:group-hover:text-emerald-400">
              {item.icon}
            </span>
            <span className="hidden lg:block">{item.label}</span>
          </div>
          <FaChevronDown
            className={`text-xs transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isOpen && (
          <div className="pl-6 mt-1 space-y-1 border-l border-slate-200 dark:border-slate-700">
            {item.children.map((child) => (
              <SidebarItem
                key={child.id}
                item={child}
                activeTab={activeTab}
                onOpenTab={onOpenTab}
                hasPermission={hasPermission}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => onOpenTab(item)}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium text-sm transition-all relative group justify-center lg:justify-start
        ${
          isActive
            ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/10"
            : "text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800"
        }`}
    >
      <span
        className={`text-lg flex-shrink-0 transition-colors ${
          isActive
            ? "text-white"
            : "text-slate-400 group-hover:text-emerald-500 dark:group-hover:text-emerald-400"
        }`}
      >
        {item.icon}
      </span>
      <span className="whitespace-nowrap hidden lg:block text-xs">
        {item.label}
      </span>
    </button>
  );
};

// ========================================================
// Sidebar
// ========================================================
const Sidebar = ({ user, onOpenTab, activeTab }) => {
  const { hasPermission } = usePermissions(user);
  const filteredMenu = filterMenu(MENU.ROLE_ADMIN, hasPermission);
  const { logoSrc, name } = getTenantBranding(user);

  return (
    <aside
      className="hidden md:flex flex-col h-full w-20 lg:w-64 transition-all duration-300 ease-in-out select-none
      bg-white border-r border-slate-200 text-slate-700
      dark:bg-slate-900 dark:border-slate-800 dark:text-slate-200"
    >
      <div className="h-16 flex items-center px-4 border-b border-slate-200 dark:border-slate-800 gap-3 justify-center lg:justify-start">
        <div className="flex h-9 w-16 shrink-0 items-center justify-center">
          <img
            src={logoSrc}
            alt={name}
            className="max-h-9 w-auto object-contain"
          />
        </div>
        <span className="hidden lg:block ml-3 font-bold tracking-wide text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
          {name}
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {filteredMenu.map((item) => (
          <SidebarItem
            key={item.id}
            item={item}
            activeTab={activeTab}
            onOpenTab={onOpenTab}
            hasPermission={hasPermission}
          />
        ))}
      </nav>
    </aside>
  );
};
// ========================================================
// Drawer
// ========================================================
const DrawerItem = ({ item, activeTab, onOpenTab, hasPermission }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isActive = activeTab === item.id;
  const [isOpen, setIsOpen] = useState(false);

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-3 py-3 rounded-xl font-medium text-sm transition-all
            text-slate-500 hover:text-slate-900 hover:bg-slate-100
            dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800 group"
        >
          <div className="flex items-center gap-3">
            <span className="text-lg">{item.icon}</span>
            <span>{item.label}</span>
          </div>
          <FaChevronDown
            className={`text-xs transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {isOpen && (
          <div className="pl-6 mt-1 space-y-1 border-l border-slate-200 dark:border-slate-700">
            {item.children.map((child) => (
              <DrawerItem
                key={child.id}
                item={child}
                activeTab={activeTab}
                onOpenTab={onOpenTab}
                hasPermission={hasPermission}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => onOpenTab(item)}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium text-sm transition-all relative group
        ${
          isActive
            ? "bg-emerald-600 text-white"
            : "text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800"
        }`}
    >
      <span className={`text-lg ${isActive ? "text-white" : ""}`}>
        {item.icon}
      </span>
      <span>{item.label}</span>
    </button>
  );
};

const SidebarDrawer = ({ open, onClose, user, onOpenTab, activeTab }) => {
  const { hasPermission } = usePermissions(user);
  const filteredMenu = filterMenu(MENU.ROLE_ADMIN, hasPermission);
  const { logoSrc, name } = getTenantBranding(user);

  return (
    <div
      className={`fixed inset-0 z-50 lg:hidden ${
        open ? "" : "pointer-events-none"
      }`}
    >
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-slate-900/50 dark:bg-black/60 transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />

      <aside
        className={`absolute left-0 top-0 h-full w-72 flex flex-col shadow-2xl transition-transform duration-200 select-none
        bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200
        ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-16 shrink-0 items-center justify-center">
              <img
                src={logoSrc}
                alt={name}
                className="max-h-9 w-auto object-contain"
              />
            </div>
            <span className="font-bold tracking-wide text-emerald-600 dark:text-emerald-400">
              {name}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-slate-800"
          >
            <FaTimes />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {filteredMenu.map((item) => (
            <DrawerItem
              key={item.id}
              item={item}
              activeTab={activeTab}
              onOpenTab={(menuItem) => {
                onOpenTab(menuItem);
                onClose();
              }}
              hasPermission={hasPermission}
            />
          ))}
        </nav>
      </aside>
    </div>
  );
};

// ========================================================
// Topbar
// ========================================================
// ========================================================
// Topbar
// ========================================================
const Topbar = ({ user, onLogin, onLogout, onMenuClick, activeTab }) => {
  const [lang, setLang] = useState("EN");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();
  const { logoSrc, name } = getTenantBranding(user);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    onLogout?.();
    navigate("/");
  };

  const displayTitle = activeTab
    ? activeTab
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
    : "Dashboard";

  return (
    <header
      className="h-16 sticky top-0 z-40 flex items-center justify-between px-3 md:px-6 select-none backdrop-blur-md
      border-b border-slate-200/80 bg-white/80
      dark:border-slate-800 dark:bg-slate-900/80"
    >
      {/* LEFT SIDE */}
      <div className="flex items-center gap-2 md:gap-3 min-w-0">
        <button
          onClick={onMenuClick}
          className="md:hidden p-2 rounded-xl flex-shrink-0
            text-slate-500 border border-slate-200 hover:bg-slate-50
            dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-800"
          aria-label="Open menu"
        >
          <FaBars />
        </button>

        <h1 className="font-bold text-sm md:text-base tracking-tight hidden sm:block truncate text-slate-800 dark:text-slate-100">
          {displayTitle === "Dashboard" ? "Overview" : displayTitle}
        </h1>

        {/* Mobile logo + tenant name */}
        <div className="flex items-center gap-2 sm:hidden min-w-0">
          <div className="flex h-9 w-16 shrink-0 items-center justify-center">
            <img
              src={logoSrc}
              alt={name}
              className="max-h-9 w-auto object-contain"
            />
          </div>
          <span className="font-extrabold text-xs tracking-tight text-emerald-600 dark:text-emerald-400 uppercase truncate">
            {name}
          </span>
        </div>
      </div>

      {/* RIGHT SIDE – restored */}
      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
        <div className="relative flex items-center">
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="appearance-none rounded-xl pl-2 pr-6 py-1.5 text-[11px] font-bold cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition
              bg-slate-50 border border-slate-200 text-slate-700
              dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
          >
            <option value="EN">EN</option>
            <option value="AM">AM</option>
          </select>
          <FaChevronDown className="absolute right-2 text-[8px] text-slate-400 pointer-events-none" />
        </div>

        <ThemeToggle />

        <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-0.5 hidden sm:block" />

        {user ? (
          <div className="flex items-center gap-2 md:gap-3">
            <div className="flex items-center gap-2 pl-1">
              <div
                className="w-8 h-8 md:w-9 md:h-9 rounded-xl flex items-center justify-center text-lg shadow-sm flex-shrink-0
                bg-emerald-50 text-emerald-600 border border-emerald-100
                dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800"
              >
                {user.user?.photoUrl ? (
                  <img
                    src={user.user?.photoUrl}
                    alt="Profile"
                    className="w-full h-full rounded-xl object-cover"
                  />
                ) : (
                  <FaUserCircle />
                )}
              </div>
              <div className="hidden md:block text-left max-w-[120px]">
                <div className="text-xs font-bold leading-tight truncate text-slate-800 dark:text-slate-100">
                  {user?.user?.name}
                  {user?.tenant?.branchName && ` (${user.tenant.branchName})`}
                </div>
                <div className="text-[10px] font-medium capitalize truncate text-slate-400 dark:text-slate-500">
                  {user?.user?.roles?.[0]
                    ? String(user.user?.roles[0]?.name)
                        .toLowerCase()
                        .replace("role_", "")
                    : "viewer"}
                </div>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="p-2 rounded-xl text-xs md:text-sm transition
                border border-rose-100 hover:bg-rose-50 text-rose-500
                dark:border-rose-900/50 dark:hover:bg-rose-950/40"
              title="Sign Out"
            >
              <FaSignOutAlt />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowAuthModal(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md shadow-emerald-600/10 transition"
          >
            <FaSignInAlt className="text-xs" />
            <span className="hidden xs:inline">Sign In</span>
          </button>
        )}
      </div>
    </header>
  );
};

// ========================================================
// TabsBar
// ========================================================
const TabsBar = ({ tabs, activeTab, onSelect, onClose }) => {
  return (
    <div
      className="h-11 flex items-center px-2 md:px-4 overflow-x-auto scrollbar-hide gap-1 sticky top-16 z-30
      border-b border-slate-200/80 bg-white
      dark:border-slate-800 dark:bg-slate-900"
    >
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <div
            key={tab.id}
            onClick={() => onSelect(tab.id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap flex-shrink-0 transition-colors border ${
              isActive
                ? "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800"
                : "text-slate-500 hover:bg-slate-50 border-transparent dark:text-slate-400 dark:hover:bg-slate-800"
            }`}
          >
            <span>{tab.title}</span>
            {tab.id !== "dashboard" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose(tab.id);
                }}
                className="p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 dark:hover:text-slate-200 dark:hover:bg-slate-700"
              >
                <FaTimes className="text-[10px]" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ========================================================
// MAIN APP SHELL
// ========================================================
export default function AppShell() {
  const navigate = useNavigate();
  const GOOGLE_CLIENT_ID =
    "832174236202-mr35qcm22j6fojc755p3ioldjhsebaoi.apps.googleusercontent.com";

  const token = localStorage.getItem("token");

  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    try {
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      console.error("Failed to parse user from localStorage:", e);
      return null;
    }
  });

  const [showOnboarding, setShowOnboarding] = useState(() => {
    const savedUser = localStorage.getItem("user");
    if (!savedUser) return false;
    try {
      const parsed = JSON.parse(savedUser);
      return parsed?.data?.tenant?.completed === false;
    } catch {
      return false;
    }
  });

  const [isPendingApproval, setIsPendingApproval] = useState(() => {
    const savedUser = localStorage.getItem("user");
    if (!savedUser) return false;
    try {
      const parsed = JSON.parse(savedUser);
      return parsed?.data?.tenant?.approved === false;
    } catch {
      return false;
    }
  });

  const [tabs, setTabs] = useState([{ id: "dashboard", title: "Dashboard" }]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLoginSuccess = (userData) => {
    const targetPayload = userData?.data ? userData.data : userData;
    const isCompleted = targetPayload?.tenant?.completed;
    const isApproved = targetPayload?.tenant?.approved;

    setCurrentUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));

    const activeToken = userData?.data?.token;
    if (activeToken) localStorage.setItem("token", activeToken);

    setShowOnboarding(isCompleted === false);
    setIsPendingApproval(isApproved === false);
  };

  const handleProfileCompleted = (targetPayload) => {
    setCurrentUser(targetPayload);
    localStorage.setItem("user", JSON.stringify(targetPayload));
    if (targetPayload?.token)
      localStorage.setItem("token", targetPayload.token);
    setShowOnboarding(false);
    const isApproved = targetPayload?.data?.approved ?? targetPayload?.approved;
    setIsPendingApproval(isApproved === false);
  };

  const handleLogoutSuccess = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setCurrentUser(null);
    setShowOnboarding(false);
    setIsPendingApproval(false);
    setTabs([{ id: "dashboard", title: "Dashboard" }]);
    setActiveTab("dashboard");
    navigate("/");
  };

  useEffect(() => {
    if (!localStorage.getItem("token")) return;

    const INACTIVITY_TIME = 5 * 60 * 1000; // 5 minutes
    let timeoutId;

    const logout = () => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.setItem("logout-event", Date.now().toString());

      setCurrentUser(null);
      setShowOnboarding(false);
      setIsPendingApproval(false);
      setTabs([{ id: "dashboard", title: "Dashboard" }]);
      setActiveTab("dashboard");
      navigate("/");
    };

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(logout, INACTIVITY_TIME);
    };

    const activityEvents = [
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    activityEvents.forEach((event) =>
      window.addEventListener(event, resetTimer, { passive: true })
    );

    // Sync logout across browser tabs
    const handleStorage = (event) => {
      if (
        event.key === "logout-event" ||
        (event.key === "token" && event.newValue === null)
      ) {
        setCurrentUser(null);
        setShowOnboarding(false);
        setIsPendingApproval(false);
        setTabs([{ id: "dashboard", title: "Dashboard" }]);
        setActiveTab("dashboard");
        navigate("/");
      }
    };

    window.addEventListener("storage", handleStorage);
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      activityEvents.forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, [navigate]);

  const { hasPermission } = usePermissions(currentUser?.data || currentUser);

  const handleOpenTab = (menuItem) => {
    if (menuItem.children?.length > 0) return;
    if (menuItem.permission && !hasPermission(menuItem.permission)) {
      alert("You don't have permission to access this module.");
      return;
    }
    if (!tabs.some((tab) => tab.id === menuItem.id)) {
      setTabs([...tabs, { id: menuItem.id, title: menuItem.label }]);
    }
    setActiveTab(menuItem.id);
  };

  const handleCloseTab = (tabIdToClose) => {
    if (tabIdToClose === "dashboard") return;
    const filteredTabs = tabs.filter((tab) => tab.id !== tabIdToClose);
    setTabs(filteredTabs);
    if (activeTab === tabIdToClose) {
      const fallbackTab =
        filteredTabs[filteredTabs.length - 1] || filteredTabs[0];
      setActiveTab(fallbackTab.id);
    }
  };

  if (!token) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <HomePage onLogin={handleLoginSuccess} />
      </GoogleOAuthProvider>
    );
  }

  return (
    <div
      className="flex h-screen w-screen overflow-hidden font-sans antialiased relative
      bg-slate-100 text-slate-800
      dark:bg-slate-950 dark:text-slate-100"
    >
      <Sidebar
        user={currentUser?.data || currentUser}
        activeTab={activeTab}
        onOpenTab={handleOpenTab}
      />

      <SidebarDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        user={currentUser?.data || currentUser}
        activeTab={activeTab}
        onOpenTab={handleOpenTab}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          user={currentUser?.data || currentUser}
          onLogin={handleLoginSuccess}
          onLogout={handleLogoutSuccess}
          onMenuClick={() => setDrawerOpen(true)}
          activeTab={activeTab}
        />

        <TabsBar
          tabs={tabs}
          activeTab={activeTab}
          onSelect={setActiveTab}
          onClose={handleCloseTab}
        />

        <main
          className="flex-1 p-4 md:p-6 overflow-y-auto pb-20 md:pb-6
          bg-slate-50/50 dark:bg-slate-950"
        >
          <div className="mx-auto max-w-7xl h-full">
            {COMPONENT_MAP[activeTab] || (
              <div
                className="p-4 rounded-2xl border border-dashed text-center
                bg-white border-slate-200 text-slate-400
                dark:bg-slate-900 dark:border-slate-700 dark:text-slate-500"
              >
                Module Component ({activeTab}) Missing from Core Router Map
              </div>
            )}
          </div>
        </main>
      </div>

      <CompleteProfileModal
        isOpen={showOnboarding}
        onProfileCompleted={handleProfileCompleted}
        user={currentUser?.data}
      />

      {isPendingApproval && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 dark:bg-black/70 backdrop-blur-sm p-4 select-none">
          <div className="rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center bg-white border border-slate-100 dark:bg-slate-900 dark:border-slate-700">
            <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-100 text-amber-500 dark:bg-amber-900/20 dark:border-amber-800 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M12 15v2m0-6h.01M5.94 19.5h12.12a2 2 0 001.72-3l-6.06-10.5a2 2 0 00-3.44 0L4.22 16.5a2 2 0 001.72 3z"
                />
              </svg>
            </div>
            <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 mb-1">
              Account Under Review
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mb-5">
              Your business account registration is undergoing administrative
              verification. Full platform utilities will become available as
              soon as the review is complete.
            </p>
            <button
              onClick={handleLogoutSuccess}
              className="w-full py-2.5 px-4 rounded-xl text-xs font-bold transition
                bg-slate-100 hover:bg-slate-200 text-slate-700
                dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200"
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
